/**
 * Utilitaires pour détecter les informations du device et du navigateur
 */

interface DeviceInfo {
  deviceType: 'mobile' | 'tablet' | 'desktop';
  browserName: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  locale: string;
  language: string;
  timezone: string;
  screenWidth: number;
  screenHeight: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

/**
 * Détecter le type de device
 */
function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Tablettes
  if (
    /tablet|ipad|playbook|silk|android(?!.*mobile)/i.test(ua) ||
    (width >= 600 && width < 1024)
  ) {
    return 'tablet';
  }

  // Mobiles
  if (
    /mobile|android|iphone|ipod|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua) ||
    width < 600
  ) {
    return 'mobile';
  }

  // Desktop par défaut
  return 'desktop';
}

/**
 * Détecter le navigateur
 */
function detectBrowser(): { name: string; version: string } {
  const ua = navigator.userAgent;
  let browserName = 'Unknown';
  let browserVersion = 'Unknown';

  // Chrome
  if (ua.includes('Chrome') && !ua.includes('Edg') && !ua.includes('OPR')) {
    browserName = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Edge
  else if (ua.includes('Edg')) {
    browserName = 'Edge';
    const match = ua.match(/Edg\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Firefox
  else if (ua.includes('Firefox')) {
    browserName = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Safari
  else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browserName = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }
  // Opera
  else if (ua.includes('OPR')) {
    browserName = 'Opera';
    const match = ua.match(/OPR\/(\d+)/);
    browserVersion = match ? match[1] : 'Unknown';
  }

  return { name: browserName, version: browserVersion };
}

/**
 * Détecter l'OS
 */
function detectOS(): { os: string; version: string } {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  let version = 'Unknown';

  // Windows
  if (ua.includes('Windows')) {
    os = 'Windows';
    if (ua.includes('Windows NT 10.0')) version = '10/11';
    else if (ua.includes('Windows NT 6.3')) version = '8.1';
    else if (ua.includes('Windows NT 6.2')) version = '8';
    else if (ua.includes('Windows NT 6.1')) version = '7';
  }
  // macOS
  else if (ua.includes('Mac OS X')) {
    os = 'macOS';
    const match = ua.match(/Mac OS X (\d+)[._](\d+)/);
    if (match) version = `${match[1]}.${match[2]}`;
  }
  // iOS
  else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    const match = ua.match(/OS (\d+)[._](\d+)/);
    if (match) version = `${match[1]}.${match[2]}`;
  }
  // Android
  else if (ua.includes('Android')) {
    os = 'Android';
    const match = ua.match(/Android (\d+)[._](\d+)/);
    if (match) version = `${match[1]}.${match[2]}`;
  }
  // Linux
  else if (ua.includes('Linux')) {
    os = 'Linux';
    version = 'Unknown';
  }

  return { os, version };
}

/**
 * Obtenir toutes les informations du device
 */
export function getDeviceInfo(): DeviceInfo {
  const deviceType = detectDeviceType();
  const browser = detectBrowser();
  const osInfo = detectOS();

  return {
    deviceType,
    browserName: browser.name,
    browserVersion: browser.version,
    os: osInfo.os,
    osVersion: osInfo.version,
    locale: navigator.language || 'unknown',
    language: navigator.language.split('-')[0] || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
  };
}

